/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/*
 * Product form variant id (Apple Pay / express checkout fix).
 *
 * product-blocks.liquid renders one name="id" field inside each product form. Add to Cart and the express checkout
 * buttons both send that field, so it must always hold the size the shopper picked, however it was picked:
 * - the theme's size buttons (the theme only moves the "selected" attribute, which stops working once any script
 *   has set the field's value, so the value is set again after every theme size change);
 * - VO Product Options or another app writing the field, writing its own "vopo-id" select (or a click in VO's #bcpo
 *   picking again the size that select already holds), or replacing the field;
 * - an app adding a second name="id" field (every name="id" field of the form is kept on the same value);
 * - an app's own size radios or checkboxes named "id": the checked one is read as the size, and the matching one is
 *   checked when the size changes elsewhere. Their values are never changed.
 * It checks on every tap, click, key press, change and submit, and again right before Add to Cart or an express
 * button handles the tap, so the id sent is always the current one.
 */
(function () {
  'use strict';

  if (typeof WeakMap !== 'function' || !Element.prototype.closest) return;

  var FORM_SELECTOR = 'form.ProductForm';
  var states = new WeakMap();
  var announced = new WeakMap();
  var timer = null;
  var voTapped = false;

  function sectionOf(form) {
    return form.closest('[data-section-type]') || document;
  }

  function stateOf(form) {
    var state = states.get(form);

    if (!state) {
      state = { agreed: null, seen: new WeakMap(), vopo: undefined, hint: null, ids: null, rendered: null, checked: undefined, checkedInit: false };
      states.set(form, state);
    }

    return state;
  }

  // Reads the section's product JSON once: the product's variant ids, and the id the page was rendered with.
  function loadProduct(form, state) {
    if (state.ids) return;
    state.ids = {};

    try {
      var data = JSON.parse(sectionOf(form).querySelector('[data-product-json]').textContent);
      data.product.variants.forEach(function (variant) {
        state.ids[String(variant.id)] = true;
      });
      if (data.selected_variant_id != null) state.rendered = String(data.selected_variant_id);
    } catch (error) {}
  }

  function isVariantOf(form, state, value) {
    if (typeof value !== 'string' || value === '') return false;
    loadProduct(form, state);

    return Object.keys(state.ids).length ? state.ids[value] === true : /^\d+$/.test(value);
  }

  function idFields(form) {
    var fields = [];

    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && !isCheckable(element)) fields.push(element);
    }

    return fields;
  }

  function isCheckable(element) {
    return element.type === 'radio' || element.type === 'checkbox';
  }

  // An app's size radios (or checkboxes) named "id": their values are the sizes, so only which one is checked is read
  // or changed, never their values.
  function checkedId(form) {
    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && isCheckable(element) && element.checked) return element.value;
    }

    return undefined;
  }

  function checkMatching(form, value) {
    for (var i = 0; i < form.elements.length; i++) {
      var element = form.elements[i];
      if (element.name === 'id' && !element.disabled && isCheckable(element)) element.checked = element.value === value;
    }
  }

  function vopoValue(form) {
    var section = sectionOf(form);
    var select = section.querySelector('select[name="vopo-id"]');

    if (!select && section.getAttribute && section.getAttribute('data-section-type') === 'product') {
      select = document.querySelector('select[name="vopo-id"]');
    }

    return select ? select.value : undefined;
  }

  // Keeps exactly one option carrying the "selected" attribute (theme.js needs one) and makes it the chosen one.
  function markSelected(select, option) {
    for (var i = 0; i < select.options.length; i++) {
      var current = select.options[i];
      if (current !== option && current.hasAttribute('selected')) current.removeAttribute('selected');
    }

    if (!option.hasAttribute('selected')) option.setAttribute('selected', 'selected');
  }

  function setField(field, value) {
    if (field.tagName === 'SELECT') {
      var option = null;

      for (var i = 0; i < field.options.length; i++) {
        if (field.options[i].value === value) option = field.options[i];
      }

      if (!option) return;
      markSelected(field, option);
    }

    field.value = value;
  }

  function syncForm(form, announce) {
    var state = stateOf(form);
    var fields = idFields(form);
    var vopo = vopoValue(form);
    var checked = checkedId(form);
    var previous = state.agreed;
    var pick = null;
    var i;

    if (!fields.length) return;

    // 1. The theme reported a size change (variant:changed).
    if (state.hint && isVariantOf(form, state, state.hint)) pick = state.hint;
    state.hint = null;

    // 2. An id field was written since the last check (by an app, or by the shopper in the no-JS dropdown).
    for (i = 0; !pick && i < fields.length; i++) {
      if (state.seen.has(fields[i]) && state.seen.get(fields[i]) !== fields[i].value && isVariantOf(form, state, fields[i].value)) {
        pick = fields[i].value;
      }
    }

    // 2b. An app's size radio (or checkbox) named "id" was checked since the last check.
    if (!pick && state.checkedInit && checked !== state.checked && isVariantOf(form, state, checked)) pick = checked;

    // 3. VO Product Options' own "vopo-id" select changed since the last check, or VO was clicked (picking again the
    //    size its select already held, after a theme size change). With checked radios named "id", step 2b sees that.
    if (!pick && state.vopo !== undefined && (vopo !== state.vopo || (voTapped && checked === undefined)) && isVariantOf(form, state, vopo)) pick = vopo;

    // 4. Nothing changed: a field added by an app takes the agreed id, and a lone replaced field is trusted.
    //    If no field holds a variant of this product, nothing is written (another app may have done it on purpose).
    for (i = 0; !pick && previous && i < fields.length; i++) {
      if (fields[i].value === previous) pick = previous;
    }
    if (!pick && isVariantOf(form, state, fields[0].value)) pick = fields[0].value;

    if (pick) {
      fields.forEach(function (field) {
        if (field.value !== pick) {
          setField(field, pick);
        } else if (field.tagName === 'SELECT' && field.selectedIndex > -1) {
          markSelected(field, field.options[field.selectedIndex]);
        }
      });
      state.agreed = pick;
      checkMatching(form, pick);
      checked = checkedId(form);
    }

    state.vopo = vopo;
    state.checked = checked;
    state.checkedInit = true;
    loadProduct(form, state);

    fields.forEach(function (field) {
      state.seen.set(field, field.value);

      // A field seen for the first time counts as already announced with the previous id or, for a form seen for the
      // first time (page load, or a quick view added later), with the id the page was rendered with, which is what
      // the express buttons read when they started.
      if (!announced.has(field)) announced.set(field, previous || state.rendered || field.value);

      // Tell listeners on the form (Shopify's express buttons, Shop Pay Installments, apps) that the size changed,
      // unless a bubbling change event for this value has already been sent from this field.
      if (announce && announced.get(field) !== field.value) {
        announced.set(field, field.value);
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function syncAll(announce) {
    var forms = document.querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) syncForm(forms[i], announce);
  }

  function syncLater() {
    if (timer) return;
    timer = setTimeout(function () {
      timer = null;
      syncAll(true);
      voTapped = false;
    }, 0);
  }

  // Before anything handles a tap, click, key press or submit (Add to Cart, express buttons, sticky bars), and again
  // once the page's own handlers have run (an app may have just written the size).
  ['pointerdown', 'mousedown', 'touchstart', 'click', 'keydown', 'submit'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      syncAll(true);
      // Only a click (not a scroll or Tab key that starts on VO's size row), and only for the deferred check.
      if (type === 'click' && event.target && event.target.closest && event.target.closest('#bcpo')) voTapped = true;
      syncLater();
    }, { capture: true, passive: true });
  });

  // Change and input: check once the event has reached every listener.
  ['change', 'input'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var target = event.target;
      if (target && target.name === 'id' && event.bubbles) announced.set(target, target.value);
      syncLater();
    }, true);
  });

  // The theme fires variant:changed before it updates the field, so remember the id and set it afterwards.
  document.addEventListener('variant:changed', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !event.target || !event.target.querySelectorAll) return;

    var forms = event.target.querySelectorAll(FORM_SELECTOR);
    for (var i = 0; i < forms.length; i++) stateOf(forms[i]).hint = String(variant.id);
    syncLater();
  });

  syncAll(false);
})();
